import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import {
    AppstoreOutlined,
    CrownOutlined,
    DollarOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import { Menu, type MenuProps } from 'antd';
import cn from 'classnames';

import * as route from '@/shared/constants/router';
import { useMobileMediaQuery } from '@/shared/lib/hooks';

import cls from './affiliate-menu.module.css';

type MenuItem = Required<MenuProps>['items'][number];

export const AffiliateMenu = memo(() => {
    const { t } = useTranslation('affiliate');
    const { pathname } = useLocation();
    const isMobile = useMobileMediaQuery();

    // A referral-detail route (/affiliate/referrals/:id) keeps the Referrals tab
    // highlighted; otherwise fall back to the exact pathname.
    const selectedKeys = useMemo(() => {
        if (pathname.startsWith(route.getAffiliateReferralsPath())) {
            return [route.getAffiliateReferralsPath()];
        }

        return [pathname];
    }, [pathname]);

    const menuItems = useMemo<MenuItem[]>(
        () => [
            {
                key: route.getAffiliateDashboardPath(),
                label: (
                    <Link to={route.getAffiliateDashboardPath()}>
                        <AppstoreOutlined />
                        {t('menu.dashboard')}
                    </Link>
                ),
            },
            {
                key: route.getAffiliateReferralsPath(),
                label: (
                    <Link to={route.getAffiliateReferralsPath()}>
                        <TeamOutlined />
                        {t('menu.referrals')}
                    </Link>
                ),
            },
            {
                key: route.getAffiliateCommissionsPath(),
                label: (
                    <Link to={route.getAffiliateCommissionsPath()}>
                        <DollarOutlined />
                        {t('menu.commissions')}
                    </Link>
                ),
            },
            {
                key: route.getAffiliateCustomPlansPath(),
                label: (
                    <Link to={route.getAffiliateCustomPlansPath()}>
                        <CrownOutlined />
                        {t('menu.customPlans')}
                    </Link>
                ),
            },
        ],
        [t],
    );

    return (
        <div className={cn(cls.wrapper)}>
            <div
                className={cn(cls.menuContainer, {
                    [cls.mobileMenu]: isMobile,
                })}
            >
                <Menu
                    className={cls.menu}
                    id="AffiliateMenu"
                    items={menuItems}
                    mode="inline"
                    selectedKeys={selectedKeys}
                />
            </div>
        </div>
    );
});

AffiliateMenu.displayName = 'AffiliateMenu';
